// Each product has two representations of these flags in Algolia:
//   - a plain global boolean, e.g. `isOutlet: true`
//   - a per-market array, e.g. `outlet: ["NOK", "DKK"]` — the markets the flag
//     is true in. A product on outlet in Norway but not Denmark has `["NOK"]`.
// This maps the boolean attribute name -> the array attribute name.
export const LOCALIZED_FILTERS: Record<string, string> = {
	isOutlet: "outlet",
	isArchive: "archive",
	isFreeRecipe: "freeRecipe",
};

/**
 * Narrows the boolean-`true` flag filters in an Algolia filter string to the
 * currently selected `currency` by ANDing on the market-aware array facet.
 *
 * The boolean is KEPT, not replaced: Algolia query rules match their trigger on
 * it (e.g. the "Gratis oppskrifter" rule fires on `isFreeRecipe:true`), so
 * rewriting it away silently stops that rule — and the page's facets/pins — from
 * applying. Keeping the boolean also disambiguates overlapping rules: the more
 * specific rule only wins while its exact condition is present in the query.
 *
 * Only `:true` is rewritten. `:false` is left untouched — a rule that filters on
 * `isOutlet:false` genuinely means "the global flag is off" and stays a plain
 * boolean check, whereas "is on" is what needs to be resolved per market.
 *
 * @example
 * // currency = "NOK"
 * replaceLocalizedFilters("productType:yarn AND isOutlet:true", "NOK")
 * // -> "productType:yarn AND isOutlet:true AND outlet:'NOK'"  (rule fires + NOK only)
 *
 * replaceLocalizedFilters("productType:yarn AND isOutlet:false", "NOK")
 * // -> "productType:yarn AND isOutlet:false" (unchanged)
 */
export const replaceLocalizedFilters = (filter?: string, currency?: string) => {
	// Nothing to localize without both a filter string and a target market.
	if (!currency || !filter) return filter;

	// Matches `isOutlet:true`, `isArchive:true`, `isFreeRecipe:true`.
	// \b guards against partial matches (e.g. a longer attribute name).
	const pattern = new RegExp(`\\b(${Object.keys(LOCALIZED_FILTERS).join("|")}):true\\b`, "g");

	// Keep the boolean (so the rule still triggers) and AND on the market array,
	// which is only true when the product's market array contains the currency.
	return filter.replace(
		pattern,
		(_match, attribute: string) =>
			`${attribute}:true AND ${LOCALIZED_FILTERS[attribute]}:'${currency}'`,
	);
};
